import React, { useState, useEffect } from 'react';
import { DBMessage } from '../types';
import { getAllMessages, addMessage, updateMessage, deleteMessage } from '../services/adminApi';

const MessagesAdmin: React.FC = () => {
  const [messages, setMessages] = useState<DBMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [editingMessage, setEditingMessage] = useState<DBMessage | null>(null);
  const [editText, setEditText] = useState('');

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await getAllMessages();
      setMessages(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      await addMessage(newMessage.trim());
      setNewMessage('');
      loadMessages();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'ajout');
    }
  };

  const handleEditMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMessage || !editText.trim()) return;

    try {
      await updateMessage(editingMessage.id, editText.trim());
      setEditingMessage(null);
      setEditText('');
      loadMessages();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la modification');
    }
  };

  const handleDeleteMessage = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) return;

    try {
      await deleteMessage(id);
      loadMessages();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
    }
  };

  const startEdit = (message: DBMessage) => {
    setEditingMessage(message);
    setEditText(message.text);
  };

  const cancelEdit = () => {
    setEditingMessage(null);
    setEditText('');
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Gestion des Messages</h2>
        <span className="text-sm text-gray-500">{messages.length} message(s)</span>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* Formulaire d'ajout */}
      <form onSubmit={handleAddMessage} className="mb-6 p-4 bg-gray-50 rounded-lg">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Ajouter un nouveau message
        </label>
        <div className="flex space-x-2">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Tapez votre message ici..."
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={2}
          />
          <button
            type="submit"
            disabled={!newMessage.trim()}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Ajouter
          </button>
        </div>
      </form>

      {/* Liste des messages */}
      <div className="space-y-3">
        {messages.map((message) => (
          <div key={message.id} className="border border-gray-200 rounded-lg p-4">
            {editingMessage?.id === message.id ? (
              <form onSubmit={handleEditMessage} className="space-y-3">
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={2}
                />
                <div className="flex space-x-2">
                  <button
                    type="submit"
                    className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                  >
                    Sauvegarder
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="flex justify-between items-start">
                  <p className="text-gray-900 flex-1 mr-4">{message.text}</p>
                  <div className="flex space-x-2 flex-shrink-0">
                    <button
                      onClick={() => startEdit(message)}
                      className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                    >
                      ✏️ Modifier
                    </button>
                    <button
                      onClick={() => handleDeleteMessage(message.id)}
                      className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                    >
                      🗑️ Supprimer
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  ID: {message.id} • Créé: {new Date(message.created_at).toLocaleString('fr-FR')}
                </p>
              </>
            )}
          </div>
        ))}
      </div>

      {messages.length === 0 && !loading && (
        <div className="text-center py-8 text-gray-500">
          Aucun message trouvé. Ajoutez-en un pour commencer !
        </div>
      )}
    </div>
  );
};

export default MessagesAdmin;
